import React, { useEffect } from 'react';

import moment from 'moment';
import { generateDateInRange } from '../utils/generateDateInRange';
import { useAppDispatch, useAppSelector } from '../app/hooks';

import {
  OrderingSelectors,
  OrderingConstants,
  OrderingOperations,
  OrderingHooks,
} from 'polygon-ordering';
import { validateFutureMenuItems } from '../thunks/validateFutureMenuItems';

import DateSelector from './DateSelector';
import TimeSelector from './TimeSelector';
import DateTimeSelectorsWrapper from './DateTimeSelectorsWrapper';
import { setDeliveryTimeSelected } from '../slices/deliveryTimeSelected';

const { ASAP_TIME } = OrderingConstants;

const {
  getLocation,
  getBufferSaleType,
  getBufferLocation,
  getFutureOrderingLimitBySaleType,
  getEarliestFutureOrderingDateTime,
} = OrderingSelectors;

const { useFutureTimeSelectorMinMax: useFutureTimeSelectorMinMaxPO } = OrderingHooks;

const { fetchOrderingProviderDetails } = OrderingOperations;

//!: I think this should be from PC
const MINUTE_INTERVAL = 5;

/**
 * # Future dated ordering selector
 * Essentially a scheduled ordering selector allowing something to be ordered today
 * or another day at a user specified time.
 *
 * ### This is used for these saletypes:
 * - Catering - Pickup & Delivery
 * - Pickup (future ordering enabled)
 *
 * - Pickup:
 *    - If you are looking for the regular Pickup time picker, where it's pickup
 *    for the current day, look to this component: TodaysDateSelector
 *
 * @param time
 * @param setTime
 */
const FutureDateSelector = ({
  time,
  setTime,
  onTimeUpdatedForNewBufferLocation,
  disableCutoffTimes,
}: {
  time?: string;
  setTime?: (time: string) => void;
  onTimeUpdatedForNewBufferLocation?: () => void;
  disableCutoffTimes?: boolean;
}) => {
  const dispatch = useAppDispatch();

  const modalParams = useAppSelector(state => state.modalParams);
  const saleTypeFromBuffer = useAppSelector(getBufferSaleType);
  const saleType = modalParams?.saleType || saleTypeFromBuffer;
  /**
   * The passed in default time value is a datetime string. There may be a date but the time will be midnight by default.
   * It doesn't make sense to set the time picker to midnight by default. We use this timeTouched boolean to force the
   * time picker to show the correct time once the user interacts with it. This is better UX because the user won't
   * accidentally place an order without setting the time first.
   */
  const deliveryTimeSelected = useAppSelector(state => state.deliveryTimeSelected);

  const futureOrderingLimit = useAppSelector(
    getFutureOrderingLimitBySaleType({
      saleType,
    }),
  );

  // This is used to get the earliest and latest times:
  // If it's the initial pre screens (before it goes to menu or when the user picks a location)
  const loadedLocation = useAppSelector(getLocation);
  const bufferLocation = useAppSelector(getBufferLocation);

  const location = bufferLocation || loadedLocation;

  /**
   * For date selector
   */
  const startDate = useAppSelector(
    getEarliestFutureOrderingDateTime({
      saleType,
      location: disableCutoffTimes ? undefined : location,
    }),
  );
  const availableDates = generateDateInRange(futureOrderingLimit, saleType, startDate);

  /**
   * For time selector
   * */
  const { timeSelectorMinTime, timeSelectorMaxTime, timeNotInsideOrderingWindow } =
    useFutureTimeSelectorMinMax({
      selectedDateTime: time,
    });
  useEffect(() => {
    if (timeNotInsideOrderingWindow && typeof onTimeUpdatedForNewBufferLocation === 'function') {
      onTimeUpdatedForNewBufferLocation();
    }
  }, [onTimeUpdatedForNewBufferLocation, timeNotInsideOrderingWindow]);

  const pickValidStartDate = () => {
    const currentTime = moment();
    const valueMoment = moment(time ?? currentTime);
    if ((time === ASAP_TIME || valueMoment.isBefore(startDate)) && setTime) {
      const defaultDateTime = startDate.set({
        hour: valueMoment.hour(),
        minute: valueMoment.minute(),
      });
      if (!deliveryTimeSelected) {
        defaultDateTime.set({
          hour: currentTime.hour(),
          minute: currentTime.minute(),
        });
      }
      setTime(defaultDateTime.toISOString());
    }
  };

  useEffect(() => {
    pickValidStartDate();
  }, []);

  useEffect(() => {
    pickValidStartDate();
  }, [time]);

  const updateOrderingProviderDetails = (value: moment.Moment) => {
    const newDate = [moment(value).year(), moment(value).month(), moment(value).date()];
    dispatch(
      fetchOrderingProviderDetails({ date: moment([...newDate]).format('l'), bufferMode: true }),
    );
  };

  return (
    <DateTimeSelectorsWrapper>
      <DateSelector
        value={time}
        dates={availableDates}
        onDateChange={value => {
          if (setTime) {
            setTime(value.toISOString());
            dispatch(validateFutureMenuItems());
            if (location) {
              updateOrderingProviderDetails(value);
            }
          }
        }}
      />

      <TimeSelector
        value={time}
        setTime={timeSelectorTime => {
          dispatch(setDeliveryTimeSelected(true));
          if (setTime) {
            setTime(timeSelectorTime);
            dispatch(validateFutureMenuItems());
            if (location) {
              const value = moment(time);
              updateOrderingProviderDetails(value);
            }
          }
        }}
        earliestSelectableTime={timeSelectorMinTime.toISOString()}
        latestSelectableDate={timeSelectorMaxTime.toISOString()}
        minuteInterval={MINUTE_INTERVAL}
      />
    </DateTimeSelectorsWrapper>
  );
};

export default FutureDateSelector;

/**
 * # useFutureTimeSelectorMinMax
 *
 * When the user changes the date we need to generate new min and max times based on the buffer
 * location and location (if the user has gone passed details screens). It needs to also handle
 * scenarios where the user hasn't selected a buffer location in a buffer flow.
 *
 * > This version uses the useFutureTimeSelectorMinMax from polygon-ordering
 * > And then passes in polygon-web-ordering specific params like modalParams.
 * > Doing it this way allows useFutureTimeSelectorMinMax's logic to be shared
 * > cohesively across web and app.
 *
 * @param selectedDateTime - the input value
 * @returns [timeSelectorMinTime, timeSelectorMaxTime, timeNotInsideOrderingWindow]
 */
export function useFutureTimeSelectorMinMax({
  selectedDateTime,
  saleTypeOverride,
}: {
  selectedDateTime?: string;
  saleTypeOverride?: SALE_TYPE;
}) {
  const modalParams = useAppSelector(state => state.modalParams);
  const saleType = saleTypeOverride || modalParams?.saleType;

  return useFutureTimeSelectorMinMaxPO({
    selectedDateTime,
    loadedSaleType: saleType,
    minuteInterval: MINUTE_INTERVAL,
  });
}
